import type { Cell } from './contract.js';

/**
 * LES HABILLAGES DU SOCLE : ce qui s'AFFICHE a la place de la valeur brute,
 * quand le formatage n'est pas une decision d'app mais une regle de la
 * plateforme.
 *
 * POURQUOI ILS EXISTENT ICI. Un habillage est une FONCTION NOMMEE : la
 * declaration d'un ecran ecrit `"habillage": "euros"`, et le registre de l'app
 * fournit `euros`. C'etait tenable tant qu'une app etait ecrite a la main ;
 * une app NEE DU STUDIO n'a aucun TypeScript ou l'ecrire, donc elle ne pouvait
 * afficher ni une somme en euros, ni une date pure lisible. Chaque app ecrite
 * a la main, de son cote, recopiait les six memes lignes.
 *
 * CE QUI ENTRE ICI, ET CE QUI N'Y ENTRERA PAS. Y entre ce dont le sens est
 * porte par la PLATEFORME : une date PURE est une chaine `YYYY-MM-DD` par
 * doctrine (AGENTS.md section 7), donc la lire en francais n'est le choix de
 * personne. N'y entre pas ce qui parle du METIER (« Sony FX9 · ref. SNY-FX9 »,
 * deux initiales de categorie) : ca reste une fonction de l'app, et le registre
 * de l'app garde toujours la main sur un nom que ce catalogue porte aussi.
 */

const EUROS = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

const JOUR = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

/** Une somme, en euros, sans centimes. Vide si la valeur n'est pas un nombre. */
export function euros(valeur: Cell): string {
  return typeof valeur === 'number' ? EUROS.format(valeur) : '';
}

/**
 * Une DATE PURE (`YYYY-MM-DD`) en francais. La date se reconstruit en heure
 * LOCALE, jamais par `new Date(chaine)` que la norme interprete en UTC : a
 * l'ouest de Greenwich, le jour affiche serait la VEILLE du jour saisi.
 */
export function datePure(valeur: Cell): string {
  const texte = typeof valeur === 'string' ? valeur : '';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(texte)) return '';
  return JOUR.format(
    new Date(Number(texte.slice(0, 4)), Number(texte.slice(5, 7)) - 1, Number(texte.slice(8, 10)), 12),
  );
}

/** Un pourcentage, tel qu'il est stocke (0 a 100). */
export function pourcentage(valeur: Cell): string {
  return typeof valeur === 'number' ? `${valeur} %` : '';
}

const INSTANT = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * Un INSTANT (une date-heure, `Date`) en francais, AVEC son heure : par defaut
 * une cellule `Date` s'affiche par `toLocaleDateString()`, donc sans l'heure et
 * a la locale du navigateur. Une saisie d'heures, un rendez-vous, un pointage
 * perdent leur sens sans l'heure : c'est la plateforme qui porte ce sens
 * (AGENTS.md section 7, une date-heure est un instant absolu).
 */
export function dateHeure(valeur: Cell): string {
  return valeur instanceof Date && !Number.isNaN(valeur.getTime()) ? INSTANT.format(valeur) : '';
}

/**
 * Une DUREE stockee en MINUTES (le socle stocke `durationMinutes`, jamais des
 * heures decimales) : « 45 min », « 1 h », « 1 h 30 ». Un total en minutes
 * s'ecrit pareil, ce qui rend lisible le pied d'un tableau et le cumul d'une
 * carte liee. Une valeur qui n'est pas un nombre entier positif rend vide.
 */
export function duree(valeur: Cell): string {
  if (typeof valeur !== 'number' || !Number.isFinite(valeur) || valeur < 0) return '';
  const minutes = Math.round(valeur);
  const heures = Math.floor(minutes / 60);
  const reste = minutes % 60;
  if (heures === 0) return `${reste} min`;
  if (reste === 0) return `${heures} h`;
  return `${heures} h ${String(reste).padStart(2, '0')}`;
}

/**
 * LE CATALOGUE, PAR NOM. C'est LUI que la declaration cite, et c'est LUI que le
 * formulaire du Studio propose : les deux lisent la meme table, donc le studio
 * ne peut pas offrir un habillage que le socle ne sait pas rendre.
 */
export const HABILLAGES_SOCLE: Readonly<Record<string, (valeur: Cell) => string>> = {
  euros,
  datePure,
  dateHeure,
  duree,
  pourcentage,
};
